import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'md' | 'lg';

const VARIANTS: Readonly<Record<Variant, string>> = {
  primary: 'bg-brand text-brand-fg hover:bg-brand-hover active:bg-brand-active',
  secondary: 'bg-surface-raised text-fg border border-line hover:bg-surface-overlay',
  ghost: 'text-fg-muted hover:bg-surface-raised hover:text-fg',
  danger: 'bg-danger text-brand-fg hover:brightness-110',
};

const SIZES: Readonly<Record<Size, string>> = {
  md: 'px-4 py-2.5 text-sm',
  lg: 'px-6 py-3.5 text-base',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Blocks interaction and shows a busy label without changing the layout. */
  isLoading?: boolean;
  icon?: ReactNode;
}

/**
 * The single button in the application. Sizes are chosen so every target stays
 * comfortable on a phone held in one hand next to a dart board.
 */
export function Button({
  variant = 'primary',
  size = 'lg',
  isLoading = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled === true || isLoading}
      aria-busy={isLoading}
      className={cn(
        'touch-target inline-flex items-center justify-center gap-2.5 rounded-lg font-medium',
        'transition-colors disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {isLoading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
